import React, {useCallback, useContext, useMemo, useState} from 'react';

import isNumber from 'lodash.isnumber';
import isString from 'lodash.isstring';
import {getFileSize} from 'src/components/image-select/getImageSize';
import {FormInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {MessageContext} from 'src/contexts/message-context';
import {SectionIconType as SectionIconTypes} from 'src/openapi/admin/models/SectionIconType';
import {SectionType as SectionTypes} from 'src/openapi/admin/models/SectionType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropZone} from '@pb/kit-component-dropzone';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';


import {useGetSearches} from '../../searches/useGetSearches';
import {MAX_CONTENT_SIZE} from '../constants';
import {EditSectionSkeleton} from '../edit-section/edit-section.skeleton';
import {useGetConferenceIcons} from '../hooks/useGetConferenceIcons';

import {useAddSectionContent} from './useAddSectionContent';
import {useCreateSection} from './useCreateSection';


import type {SectionIconType} from 'src/openapi/admin/models/SectionIconType';
import type {SectionType} from 'src/openapi/admin/models/SectionType';
import type {CheckBoxChangeData} from 'src/typedef';

type FileWithPreview = File & {preview?: string};

const getFilePreview = (file: File | null) => {
    if (file && typeof (file as FileWithPreview).preview === 'string') {
        return (file as FileWithPreview).preview;
    }

    return undefined;
};

type Props = {
    conferenceId: number,
    sectionsCount: number,
    onClose: () => void,
    onAdd: () => void,
};

export const AddSectionModal = ({sectionsCount, onAdd, onClose, conferenceId}: Props) => {
    const {setSuccessMessage} = useContext(MessageContext);

    const [busy, setBusy] = useState(false);
    const [serverError, setServerError] = useState('');

    const {
        data: createData,
        setData: setCreateData,
        saveChanges: createSectionAction,
    } = useCreateSection({sectionsCount, conferenceId});

    const {
        data: sectionContent,
        setData: setSectionContent,
        isChanged: isSectionContentChanged,
        saveChanges: addSectionContent,
    } = useAddSectionContent();

    const {
        data: searches,
        isLoading: isSearchesLoading,
    } = useGetSearches({conferenceId});

    const {
        data: icons,
        isLoading: isIconsLoading,
    } = useGetConferenceIcons();

    const addSection = useCallback(async () => {
        setBusy(true);
        const response = await createSectionAction();

        if (isString(response)) {
            setServerError(response);
            setBusy(false);
            return;
        }

        if (isNumber(response)) {
            if (isSectionContentChanged) {
                const contentResponse = await addSectionContent(response);
                if (isString(contentResponse)) {
                    setServerError(contentResponse);
                    setBusy(false);
                    return;
                }
            }

            setSuccessMessage('Section added successfully');
            onAdd();
            onClose();
        }
    }, [
        setSuccessMessage,
        createSectionAction,
        isSectionContentChanged,
        addSectionContent,
        onAdd,
        onClose,
    ]);

    const changeSectionName = useCallback((sectionName: string) => {
        setCreateData(prev => ({...prev, sectionName}));
    }, [setCreateData]);

    const changeWebVersionEnabled = useCallback((changedData: CheckBoxChangeData) => {
        const webVersionEnabled = changedData.event.currentTarget.checked;
        setCreateData(prev => ({...prev, webVersionEnabled}));
    }, [setCreateData]);

    const changeMobileVersionEnabled = useCallback((changedData: CheckBoxChangeData) => {
        const mobileVersionEnabled = changedData.event.currentTarget.checked;
        setCreateData(prev => ({...prev, mobileVersionEnabled}));
    }, [setCreateData]);

    const changeSectionType = useCallback((sectionType: SectionType) => {
        setCreateData(prev => ({...prev, sectionType}));
        if (sectionType !== SectionTypes.CUSTOM) {
            setSectionContent(null);
        }
    }, [setCreateData, setSectionContent]);

    const changeIconType = useCallback((iconType: SectionIconType) => {
        setCreateData(prev => ({...prev, iconType}));
    }, [setCreateData]);

    const changeContent = useCallback((files: File[]) => {
        const [file] = files;

        if (!file) {
            setServerError('File is corrupted or has incorrect format');
            return;
        }

        if (getFileSize(file) > 10) {
            setServerError(`File size should be less than ${MAX_CONTENT_SIZE} MB`);
            return;
        }

        setSectionContent(file);
    }, [setServerError, setSectionContent]);

    const changeSearches = useCallback((ids: number[]) => {
        const newSearches = searches
            .filter(({id}) => ids.includes(id))
            .map(({id, name}) => ({id, name}));
        setCreateData(prev => ({...prev, searches: newSearches}));
    }, [searches, setCreateData]);

    const downloadContent = useCallback(() => {
        if (!sectionContent) {
            return;
        }

        const preview = getFilePreview(sectionContent);
        if (!preview) {
            return;
        }

        const anchor = document.createElement('a');
        anchor.href = preview;
        anchor.download = sectionContent.name;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }, [sectionContent]);

    const clearContent = useCallback(() => {
        setSectionContent(null);
    }, [setSectionContent]);

    const isAddDisabled = useMemo(() => {
        if (createData.sectionType === SectionTypes.CUSTOM) {
            return !sectionContent || !createData.sectionName;
        } else if (createData.sectionType === SectionTypes.SEARCH) {
            return !createData.searches?.length || !createData.sectionName;
        }
    }, [createData, sectionContent]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Add Section
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {serverError ? (
                    <Message color="error" onClose={() => setServerError('')}>
                        {serverError}
                    </Message>
                ) : null}
                <Skeleton shown={isIconsLoading || isSearchesLoading} view={<EditSectionSkeleton/>}>
                    <Group direction="v">
                        <FormInput
                            label="Section Name"
                            maxLength={128}
                            width="100%"
                            value={createData.sectionName}
                            onBlur={changeSectionName}
                        />
                        <WidthItem width="100%">
                            <Label>Versions</Label>
                            <Group>
                                <CheckBox
                                    label="Web version enabled"
                                    checked={createData.webVersionEnabled}
                                    onCheckBoxChange={changeWebVersionEnabled}
                                />
                                <CheckBox
                                    label="Mobile version enabled"
                                    checked={createData.mobileVersionEnabled}
                                    onCheckBoxChange={changeMobileVersionEnabled}
                                />
                            </Group>
                        </WidthItem>
                        <WidthItem width="100%">
                            <Label>Section Type</Label>
                            <ComboBox value={createData.sectionType} onChange={changeSectionType}>
                                <ComboboxItem caption="Custom" value={SectionTypes.CUSTOM}/>
                                <ComboboxItem caption="Search" value={SectionTypes.SEARCH}/>
                            </ComboBox>
                        </WidthItem>
                        <WidthItem width="100%">
                            <Label>Icon Type</Label>
                            <RadioButtons
                                name="iconType"
                                onChange={changeIconType}
                                value={createData.iconType}>
                                <ResponsiveElementGroup padding="0 0 16px 0">
                                    {Object.values(SectionIconTypes).map(type => (
                                        <RadioButton
                                            key={type}
                                            name={type}
                                            value={type}
                                            label={(
                                                <WidthItem width={70} key={type}>
                                                    <img
                                                        alt={type}
                                                        src={icons.find(({iconType}) => iconType === type)?.value}
                                                        width="25"
                                                    />
                                                </WidthItem>
                                            )}
                                        />
                                    ))}
                                </ResponsiveElementGroup>
                            </RadioButtons>
                        </WidthItem>
                        {createData.sectionType === SectionTypes.CUSTOM && (
                            <WidthItem width="100%">
                                <Label>Section Content</Label>
                                {sectionContent ? (
                                    <Group justify="space-between">
                                        <WidthItem width={300}>
                                            <EllipsisTextHint title={sectionContent.name}>
                                                {sectionContent.name}
                                            </EllipsisTextHint>
                                        </WidthItem>
                                        <Group>
                                            <BasicIconButton
                                                onClick={downloadContent}
                                                iconProps={{type: Icons.DOWNLOAD, size: 'Size_16'}}
                                            />
                                            <BasicIconButton
                                                iconProps={{type: Icons.CLOSE, size: 'Size_16'}}
                                                onClick={clearContent}
                                            />
                                        </Group>
                                    </Group>
                                ) : (
                                    <DropZone
                                        accept="application/pdf"
                                        mix=""
                                        multiple={false}
                                        onDrop={changeContent}>
                                        <Group direction="v">
                                            <div>Drag & drop a file to upload or <a>browse</a></div>
                                            <div className="disabled font-size font-size_footer">
                                                Max. file size: {MAX_CONTENT_SIZE} MB. Accepted formats: PDF
                                            </div>
                                        </Group>
                                    </DropZone>
                                )}
                            </WidthItem>
                        )}
                        {createData.sectionType === SectionTypes.SEARCH && (
                            <WidthItem width="100%">
                                <Label>Select Searches</Label>
                                <ComboBox
                                    value={createData.searches?.map(({id}) => id)}
                                    multiselectable={true}
                                    disabled={!searches.length}
                                    onChange={changeSearches}>
                                    {searches.map(({id, name}) => (
                                        <ComboboxItem caption={name} value={id} key={id}/>
                                    ))}
                                </ComboBox>
                            </WidthItem>
                        )}
                    </Group>
                </Skeleton>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button busy={busy} variant="Type.PRIMARY" onClick={addSection} disabled={isAddDisabled}>
                        Add Section
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
